import { Overlay, Popover } from 'react-bootstrap';
import { Icon, RobotoFontFamily, Typography } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';
import { Layers } from '@web/vin-sessions/assets/Icons';
import React, { useState } from 'react';
import colors from '@packages/core/styles/colors';
import { useAuthState } from '@packages/contexts/auth';
import { KeywordPreview } from '../stis-publications/document-preview-modal';
import { handleLogin } from '@web/views/utils';
import { useNavigate } from 'react-router-dom';

export const HoverModal = ({
    title,
    description,
    creationDate,
    version,
    toolNumber,
    applicability,
    index,
    onClick,
    visibilityLevel,
    disabled,
}: {
    title: string;
    description: string;
    creationDate: string;
    version: string;
    toolNumber: string;
    applicability: string;
    index: number;
    onClick: () => void;
    visibilityLevel?: string;
    disabled?: boolean;
}) => {
    const useStyles = createUseStyles({
        title: {
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
        },
        popOver: {
            minWidth: 400,
            color: '#333',
            border: '1px solid #ddd',
            borderRadius: 8,
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            padding: 15,
            zIndex: 3,
        },
        banner: {
            padding: 16,
            display: 'flex',
            borderRadius: 8,
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: colors.blueOne,
        },
        description: {
            border: '1px solid #e0e0e0',
            padding: 10,
            borderRadius: 5,
            backgroundColor: '#f7f7f7',
            fontFamily: RobotoFontFamily.Regular,
            fontWeight: '400',
            fontSize: 15,
            maxHeight: 224,
            overflowY: 'auto !important',
            '&::-webkit-scrollbar': {
                width: 10,
            },
            '&::-webkit-scrollbar-track': {
                backgroundColor: '#f7f7f7',
            },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: colors.grayThree,
                borderRadius: 10,
                border: '2px solid #f7f7f7',
            },
        },
        layers: {
            display: 'flex',
            alignItems: 'center',
            fontWeight: '400',
            fontSize: 15,
            gap: 5,
        },
        titleContainer: {
            maxWidth: 200,
            fontSize: 15,
            color: '#007BFF',
            cursor: 'pointer',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            '@media (max-width: 600px)': {
                maxWidth: 150,
            },
            '@media (max-width: 400px)': {
                maxWidth: 100,
            },
            '@media (min-width: 1440px)': {
                maxWidth: 300,
            },
        },
    });

    const classes = useStyles();
    const { account, showUnauthorizedPrompt } = useAuthState();
    const navigate = useNavigate();
    const [show, setShow] = useState(false);
    const [target, setTarget] = useState<HTMLElement | null>(null);

    const handleMouseEnter = (event: React.MouseEvent<HTMLSpanElement>) => {
        setTarget(event.currentTarget as HTMLElement);
        setShow(true);
    };

    const handleMouseLeave = () => {
        setShow(false);
    };

    const typographyStyles = {
        paragraphStyle: {
            width: '100%',
            display: 'inline-block',
            marginBottom: 0,
            fontSize: 15,
            fontWeight: 400,
            fontFamily: RobotoFontFamily.Regular,
        } as React.CSSProperties,
    };

    const renderPopover = (
        <Popover
            id="popover"
            data-testid="hover-popover"
            className={classes.popOver}
            onMouseEnter={() => setShow(true)}
            onMouseLeave={handleMouseLeave}
        >
            <Popover.Content style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <p>
                    <strong style={{ fontSize: 15 }}>{title}</strong>
                </p>
                {!account && visibilityLevel !== 'FREE' && (
                    <div className={classes.banner}>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
                            <Icon name="info" color="white" size={20} />
                            <Typography variant="labelRegular" color="white" style={{ marginTop: 1 }}>
                                To access, please{' '}
                                <a
                                    href="#"
                                    style={{
                                        color: '#fff',
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        fontWeight: 500,
                                        borderBottom: '1px dotted #fff',
                                    }}
                                    onClick={(e: any) => {
                                        e.preventDefault();
                                        handleLogin(showUnauthorizedPrompt, navigate);
                                    }}
                                >
                                    Sign in
                                </a>
                            </Typography>
                        </div>
                    </div>
                )}
                <p>
                    <strong>Creation Date</strong> {creationDate}
                </p>
                <p className={classes.description}>
                    <KeywordPreview headline={description} style={typographyStyles.paragraphStyle} />
                </p>
                <div className={classes.layers}>
                    <Layers />
                    <Typography variant="default">Version {version}</Typography>
                </div>
                {toolNumber !== '-' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, justifyContent: 'center' }}>
                        <p>
                            <strong>Tool Numbers</strong>
                        </p>
                        <Typography variant="default">{toolNumber}</Typography>
                    </div>
                )}
                {applicability !== '-' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, justifyContent: 'center' }}>
                        <p>
                            <strong>APPLICABILITY:</strong>
                        </p>
                        <Typography variant="default">{applicability}</Typography>
                    </div>
                )}
            </Popover.Content>
        </Popover>
    );

    return (
        <div>
            <div style={{ display: 'flex', gap: 8 }}>
                <div
                    className={classes.titleContainer}
                    style={{
                        // Disabled (e.g. SOFTWARE_UPDATE) renders as plain, non-link text with no
                        // open-on-click — those docs are download-only. Hover info still works.
                        color: disabled ? '#4C525D' : !account && visibilityLevel !== 'FREE' ? '#8FA3BC' : '#007BFF',
                        cursor: disabled
                            ? 'default'
                            : account || (!account && visibilityLevel === 'FREE')
                              ? 'pointer'
                              : 'not-allowed',
                    }}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onClick={disabled ? undefined : onClick}
                >
                    {title}
                </div>
                {!account && visibilityLevel !== 'FREE' && (
                    <Icon name="lock-closed" color="grayFive" style={{ marginBottom: 0 }} />
                )}
            </div>

            {
                <Overlay show={show} target={target} placement="right" onHide={() => setShow(false)}>
                    {renderPopover}
                </Overlay>
            }
        </div>
    );
};
